import {useMemo} from 'react';
import {useSelector} from 'react-redux';

import {portIncidentAPI} from 'features/port/api/portIncidentAPI';
import {portMonitorAPI} from 'features/port/api/portMonitorAPI';
import {PORT_INCIDENT_FILTER_KEYS, PORT_INCIDENTS_ORDER_FIELDS} from 'features/port/lib/portFilters';
import useOpenState from 'shared/lib/hooks/useOpenState';
import {usePlugState, PlugState, userSelector, useHasTariffLimit} from 'entities/user';
import useResetPageOnEmpty from 'shared/lib/hooks/useResetPageOnEmpty';
import IOrderBy from 'shared/model/IOrderBy';
import {SetOrderByType} from 'shared/ui/Table/Table';
import IPagination from 'shared/api/model/IPagination';
import useListParams from 'shared/lib/hooks/useListParams';
import {getPollingInterval} from 'shared/api/pollingHelper';

import IPortIncident from './PortIncident/IPortIncident';
import {IPortMonitorListItem} from './PortMonitor/IPortMonitor';
import useCanCreatePortMonitor from './useCanCreatePortMonitor';

const MONITORS_LIMIT = 200;

export interface IUsePortIncidents {
    timezone: string | null | undefined,
    sort: IOrderBy | undefined,
    setSort: SetOrderByType,
    search: string,
    onSearch: (_search: string) => void,
    monitors: IPortMonitorListItem[],
    pagination: IPagination<IPortIncident> | undefined,
    isLoading: boolean,
    isFetching: boolean,
    unresolvedCount: number | undefined,
    resolvedCount: number | undefined,
    plugState: PlugState,
    handleRefresh: () => void,
    handleCreate: () => void,
    isCreateOpen: boolean,
    closeCreate: () => void,
    isExceededOpen: boolean,
    closeExceeded: () => void,
}

export const usePortIncidents = (): IUsePortIncidents => {
    const {args, sort, setSort, hasFilters, search, onSearch} = useListParams({
        filterKeys: PORT_INCIDENT_FILTER_KEYS,
        sortFields: PORT_INCIDENTS_ORDER_FIELDS,
    });
    const create = useOpenState();
    const exceeded = useOpenState();

    const user = useSelector(userSelector);
    const hasAccess = useHasTariffLimit('port_monitor_max_count_limit');
    const canCreate = useCanCreatePortMonitor();

    const {data: monitorsPagination} = portMonitorAPI.useGetPortMonitorsQuery(
        {limit: MONITORS_LIMIT},
        {skip: !hasAccess},
    );
    const monitors = useMemo(() => monitorsPagination?.items ?? [], [monitorsPagination]);

    const {data: pagination, isLoading, isFetching, refetch} = portIncidentAPI.useGetPortIncidentsQuery(
        args,
        {skip: !hasAccess, pollingInterval: getPollingInterval()},
    );

    const {data: summary, refetch: refetchSummary} = portIncidentAPI.useGetSummaryPortIncidentQuery(
        undefined,
        {skip: !hasAccess, pollingInterval: getPollingInterval()},
    );

    const isRedirecting = useResetPageOnEmpty(pagination);

    const plugState = usePlugState({
        tariffField: 'port_monitor_max_count_limit',
        isLoading: isLoading || isRedirecting,
        isEmpty: pagination?.items?.length === 0 && pagination?.totalItems === 0,
        hasFilters,
    });

    const handleRefresh = () => {
        refetch();
        refetchSummary();
    };

    const handleCreate = () => {
        if (!canCreate) {
            exceeded.open();
            return;
        }
        create.open();
    };

    return {
        timezone: user?.timezone,
        sort,
        setSort,
        search,
        onSearch,
        monitors,
        pagination,
        isLoading,
        isFetching,
        unresolvedCount: summary?.unresolved,
        resolvedCount: summary?.resolved,
        plugState,
        handleRefresh,
        handleCreate,
        isCreateOpen: create.isOpen,
        closeCreate: create.close,
        isExceededOpen: exceeded.isOpen,
        closeExceeded: exceeded.close,
    };
};

export default usePortIncidents;
